<template>
  <WdsModal :show="show" :title="$t('Delete Webhook')" @cancel="show = false">
    <WdsStack
      justify-center
      align-center
      direction-column
      gap-md
      class="content"
      data-test="modal-delete-webhook"
    >
      <div class="icon-outer">
        <div class="icon-inner">
          <WdsIcon name="trash-can" color="#D12223" :size="24"></WdsIcon>
        </div>
      </div>
      <WdsText large>
        {{ $t('Are you sure you want to delete this webhook?') }}
      </WdsText>
      <WdsText secondary>
        <BaseHtml
          :html="
            $t('This action <strong>cannot be undone</strong> and it will permanently delete it')
          "
        />
      </WdsText>
      <WdsStack justify-space-between align-center class="tw-w-full">
        <WdsButton :disabled="loading" @click="show = false">
          {{ $t('Cancel') }}
        </WdsButton>
        <WdsButton
          :disabled="loading"
          error
          data-test="delete-webhook-button"
          @click.prevent="deleteWebhook"
        >
          {{ $t('Delete') }}
          <WdsSpinner v-if="loading" indeterminate class="tw-m-0.5" />
        </WdsButton>
      </WdsStack>
    </WdsStack>
  </WdsModal>
</template>

<script setup lang="ts">
import {
  WdsText,
  WdsStack,
  WdsIcon,
  WdsModal,
  WdsButton,
  WdsSpinner,
} from '@worksome/wds-components'
import { useMutation } from '@vue/apollo-composable'
import { computed } from 'vue'
import BaseHtml from '@/components/BaseHtml.vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { FragmentType, useFragment } from '@/gql'
import { WebhookFragment } from '@/components/Integrations/Webhooks/graphql/fragments'
import NetworkError from '@/utils/notification/NetworkError'
import { deleteWebhookMutation } from '@/components/Integrations/Webhooks/graphql/mutations'
import useApolloError from '@/composables/useApolloError'

const clientId = 'publicApi'

const show = defineModel<boolean>('show')
const webhook = defineModel<FragmentType<typeof WebhookFragment> | null>('webhook')

const { mutate, loading, error } = useMutation(deleteWebhookMutation, {
  clientId,
  refetchQueries: ['Webhooks'],
})

const webhookId = computed(() => useFragment(WebhookFragment, webhook.value)?.id)

const { validationErrors } = useApolloError(error)

const deleteWebhook = async () => {
  try {
    await mutate({ input: { id: webhookId.value ?? '' } })
    show.value = false
  } catch (ex: any) {
    const msg = validationErrors.value?.['input.id'] ?? $t('Oops an error occurred')
    new NetworkError({ ex, msg }).dispatch()
  }
}
</script>

<style lang="scss" scoped>
.content {
  :deep(strong) {
    color: black;
  }

  .icon-outer {
    border-radius: 50%;
    background-color: #fdf2f2;
    padding: 6px;
    .icon-inner {
      padding: 5px;
      border-radius: 50%;
      background-color: #f9e0e0;
    }
  }
}
</style>
